/**
 * Client-side card catalog.
 *
 * The whole printable card pool is ~1000 cards / ~1.5MB from `/api/all-cards`,
 * which is small enough to hold in memory and filter in the browser. Doing so
 * is what lets the deck builder filter as you type and offer faceted dropdowns
 * (see useCardFilters) — neither is possible against `/api/search-cards`, which
 * requires a query and pages 20 rows at a time.
 *
 * Cards are returned in the RAW api shape with derived filter fields bolted on,
 * deliberately: `normalizeCard`, `getProxyImageUrl` and the card-preview panel
 * all read `card.number` / `card.images` / `card.feature.value` directly, so a
 * remapped shape would break every consumer downstream of the search pane.
 */

// Bump when the derived-field shape below changes, so a cached payload from an
// older build is discarded instead of being read with missing fields.
const CACHE_VERSION = 'v1';
const CACHE_PREFIX = `gg_card_catalog_${CACHE_VERSION}`;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const cacheKey = (language) => `${CACHE_PREFIX}_${language}`;
const cacheStampKey = (language) => `${CACHE_PREFIX}_${language}_at`;

/** In-memory catalog per language — avoids re-parsing 1.5MB of JSON on remount. */
const memoryCache = new Map();
/** In-flight requests per language, so concurrent callers share one fetch. */
const inFlight = new Map();

/**
 * Placeholder character values in the feed. These are real rows, but the
 * "character" is a dash or blank, which makes for a useless dropdown entry.
 */
const NON_CHARACTER = new Set(['', '-', '–', '—', 'and', 'unknown']);

/**
 * Display order for the Card Type and Type dropdowns. Options outside these
 * lists still work (they sort to the end) — the order is presentation only, so
 * a newly-introduced type never disappears from the UI.
 */
export const CARD_TYPE_ORDER = ['Ultra Hero', 'Ultra Mech', 'Kaiju', 'Scene'];
export const BATTLE_TYPE_ORDER = [
  'BASIC', 'POWER', 'SPEED', 'ARMED', 'INVASION', 'DEVASTATION', 'HAZARD', 'METEO',
];
/** Rarity display order, most common first. */
export const RARITY_ORDER = ['C', 'U', 'R', 'RR', 'RRR', 'RRRR', 'SEC'];

/** The label a `{value, description}` feed field should be filtered/shown by. */
function labelOf(field) {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field.description || field.value || '';
}

/**
 * Attach the fields the filter UI works in. Two of these matter beyond
 * convenience:
 *
 * - We key Card Type / Type / Rarity off `description`, not `value`. The feed
 *   ships distinct values that mean one thing — `disaster` and `hazard` both
 *   describe as HAZARD, and several rarities carry a null value with a real
 *   description. Filtering on value splits or drops those cards.
 * - `searchText` is precomputed once here rather than per keystroke, so typing
 *   into the search box stays a cheap substring scan over ~1000 strings.
 */
export function decorateCard(card) {
  const character = (card.characterName || '').trim();
  const round =
    card.round != null && `${card.round}` !== '' ? Number(card.round) : null;

  return {
    ...card,
    cardTypeLabel: labelOf(card.feature),
    battleTypeLabel: labelOf(card.type),
    rarityLabel: labelOf(card.rarity),
    characterLabel: NON_CHARACTER.has(character.toLowerCase()) ? '' : character,
    // Scenes carry a field-round requirement (0–4) and no level; everything
    // else is the reverse. The Level filter slot swaps between the two.
    roundValue: Number.isFinite(round) ? round : null,
    levelValue: card.level != null && card.level > 0 ? Number(card.level) : null,
    searchText: [card.name, card.number, card.characterName, card.effect]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  };
}

function readCache(language) {
  try {
    const stamp = localStorage.getItem(cacheStampKey(language));
    if (!stamp) return null;
    if (Date.now() - Number(stamp) > CACHE_TTL_MS) return null;

    const raw = localStorage.getItem(cacheKey(language));
    if (!raw) return null;

    const cards = JSON.parse(raw);
    return Array.isArray(cards) && cards.length > 0 ? cards : null;
  } catch {
    // Corrupt entry or unavailable storage — fall through to a network fetch.
    return null;
  }
}

function writeCache(language, cards) {
  try {
    localStorage.setItem(cacheKey(language), JSON.stringify(cards));
    localStorage.setItem(cacheStampKey(language), String(Date.now()));
  } catch {
    // Quota exceeded / private mode. The in-memory cache still serves this
    // session; the only cost is a refetch next load.
  }
}

/** Drop every cached language. Used by the explicit "refresh cards" action. */
export function clearCardCatalogCache() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable — nothing cached to clear.
  }
  memoryCache.clear();
}

/**
 * Load the full card pool for a language.
 *
 * Resolution order is memory → localStorage → network. Concurrent callers for
 * the same language share a single request.
 *
 * @param {string} language  Feed language code (e.g. 'EN').
 * @param {{ force?: boolean }} [options]  `force` bypasses both caches.
 * @returns {Promise<Array>} Decorated cards (see decorateCard).
 */
export async function fetchCardCatalog(language, options = {}) {
  const { force = false } = options;

  if (!force) {
    const memory = memoryCache.get(language);
    if (memory) return memory;

    const cached = readCache(language);
    if (cached) {
      memoryCache.set(language, cached);
      return cached;
    }

    const pending = inFlight.get(language);
    if (pending) return pending;
  }

  const request = (async () => {
    const response = await fetch(
      `/api/all-cards?lang=${encodeURIComponent(language)}`
    );
    if (!response.ok) {
      throw new Error(`Failed to load cards (${response.status})`);
    }

    const data = await response.json();
    const cards = (data.cards || []).map(decorateCard);

    memoryCache.set(language, cards);
    writeCache(language, cards);
    return cards;
  })();

  inFlight.set(language, request);
  try {
    return await request;
  } finally {
    inFlight.delete(language);
  }
}
